import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Clock, CalendarDays, ArrowLeft, Layers, TrendingUp, TrendingDown, BarChart3, Trophy, Zap } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api } from "../lib/api";
import { usePlatform, platformStyle } from "../lib/ridersPlatform";
import { MonthArchiveBar } from "./MonthArchiveBar";
import { Input } from "./ui/input";

const nf = (n) => Number(n || 0).toLocaleString("en-US");

// Cross-platform overview: each app's totals side by side. Clicking a card drills into that app.
export const UnifiedRidersView = () => {
  const [, setPlatform] = usePlatform();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const useRange = !!(range.from && range.to);
  const fromP = useRange ? range.from : undefined;
  const toP = useRange ? range.to : undefined;
  const monthP = useRange ? undefined : month;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.ridersPlatformsSummary(fromP, toP, monthP)
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [month, fromP, toP, monthP]);

  if (loading) return <div className="py-16 text-center text-slate-400" data-testid="unified-loading">جارٍ التحميل…</div>;
  if (!data) return null;

  const total = data.total || {};
  const platforms = data.platforms || [];

  return (
    <div className="space-y-4" data-testid="unified-riders-view">
      <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-semibold text-slate-500">فترة المقارنة</p>
          <MonthArchiveBar months={data.available_months || []} value={month}
            onChange={(m) => { setRange({ from: "", to: "" }); setMonth(m); }} />
        </div>
        <div className="flex items-center gap-2 flex-wrap border-t border-slate-100 pt-3" data-testid="unified-custom-range">
          <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> فترة مخصّصة</span>
          <Input type="date" data-testid="unified-range-from" value={range.from} max={range.to || undefined}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="h-8 rounded-lg border border-slate-200 px-2 text-xs font-mono-nums w-auto" dir="ltr" />
          <span className="text-slate-400 text-xs">—</span>
          <Input type="date" data-testid="unified-range-to" value={range.to} min={range.from || undefined}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="h-8 rounded-lg border border-slate-200 px-2 text-xs font-mono-nums w-auto" dir="ltr" />
          {useRange && (
            <button type="button" data-testid="unified-range-clear" onClick={() => setRange({ from: "", to: "" })}
              className="h-8 px-2 rounded-lg text-xs text-rose-600 hover:bg-rose-50">مسح</button>
          )}
        </div>
      </div>
      <div className="rounded-2xl p-4 text-white" style={{ background: "linear-gradient(90deg,#1e3a8a,#2563eb)" }}>
        <div className="flex items-center gap-2 text-white/80 text-sm"><Layers className="h-4 w-4" /> إجمالي كل التطبيقات</div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <div>
            <div className="font-mono-nums text-2xl md:text-3xl font-extrabold">{nf(total.completed)}</div>
            <div className="text-xs text-white/70">الطلبات المكتملة</div>
          </div>
          <div>
            <div className="font-mono-nums text-2xl md:text-3xl font-extrabold">{nf(total.riders)}</div>
            <div className="text-xs text-white/70">إجمالي المناديب</div>
          </div>
          <div>
            <div className="font-mono-nums text-2xl md:text-3xl font-extrabold">{nf(total.hours)}</div>
            <div className="text-xs text-white/70">إجمالي الساعات</div>
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-500 font-semibold">اختر تطبيقاً لعرض تفاصيله</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {platforms.map((p) => {
          const st = platformStyle(p.platform);
          return (
            <motion.button
              key={p.platform}
              data-testid={`unified-platform-${p.platform}`}
              onClick={() => setPlatform(p.platform)}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.98 }}
              className="text-start rounded-2xl border-2 bg-white p-4 transition-all hover:shadow-lg"
              style={{ borderColor: st.ring }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {st.logo
                    ? <img src={st.logo} alt={p.platform} className="h-8 w-8 rounded-lg object-contain" />
                    : <span className="text-2xl">{st.emoji}</span>}
                  <span className="font-heading font-bold" style={{ color: st.text }}>{p.platform}</span>
                </div>
                <ArrowLeft className="h-4 w-4 text-slate-300" />
              </div>
              <div className="mt-3 font-mono-nums text-3xl font-extrabold" style={{ color: st.color }}>{nf(p.completed)}</div>
              <div className="text-xs text-slate-400">الطلبات المكتملة</div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-slate-50 py-1.5">
                  <div className="flex items-center justify-center gap-1 text-slate-700 font-bold text-sm"><Users className="h-3.5 w-3.5 text-slate-400" />{nf(p.riders)}</div>
                  <div className="text-[10px] text-slate-400">مندوب</div>
                </div>
                <div className="rounded-lg bg-slate-50 py-1.5">
                  <div className="flex items-center justify-center gap-1 text-slate-700 font-bold text-sm"><Clock className="h-3.5 w-3.5 text-slate-400" />{nf(p.hours)}</div>
                  <div className="text-[10px] text-slate-400">ساعة</div>
                </div>
                <div className="rounded-lg bg-slate-50 py-1.5">
                  <div className="flex items-center justify-center gap-1 text-slate-700 font-bold text-sm"><CalendarDays className="h-3.5 w-3.5 text-slate-400" />{nf(p.days)}</div>
                  <div className="text-[10px] text-slate-400">يوم</div>
                </div>
              </div>
              {(() => {
                const share = total.completed > 0 ? (p.completed / total.completed) * 100 : 0;
                return (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
                      <span>الحصة من إجمالي الطلبات</span>
                      <span className="font-mono-nums font-bold" style={{ color: st.color }}>{share.toFixed(1)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${share}%`, background: st.color }} />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                      <div className="rounded-lg bg-slate-50 py-1">
                        <div className="font-mono-nums font-bold text-sm text-slate-700">{p.riders > 0 ? nf(Math.round(p.completed / p.riders)) : "—"}</div>
                        <div className="text-[10px] text-slate-400">طلب / مندوب</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 py-1">
                        <div className="font-mono-nums font-bold text-sm text-slate-700">{p.hours > 0 ? (p.completed / p.hours).toFixed(1) : "—"}</div>
                        <div className="text-[10px] text-slate-400">طلب / ساعة</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {!p.has_data && <div className="mt-2 text-[11px] text-amber-600">لا توجد بيانات مرفوعة بعد</div>}
            </motion.button>
          );
        })}
      </div>
      <PlatformsInsights platforms={platforms} total={total} month={monthP} from={fromP} to={toP} />
      <PlatformTrendChart month={monthP} from={fromP} to={toP} />
    </div>
  );
};

const PlatformTrendChart = ({ month, from, to }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.ridersPlatformsTrend(month, from, to).then((r) => { if (alive) setData(r); }).catch(() => {});
    return () => { alive = false; };
  }, [month, from, to]);
  if (!data || !data.series?.length || data.series.length < 2) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="platform-trend-chart">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-blue-600" />
        <h4 className="font-heading font-bold text-sm text-slate-700">مقارنة نمو الطلبات بين التطبيقات</h4>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data.series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(d) => String(d).slice(5)} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} width={44} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #e2e8f0" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {data.platforms.map((p) => (
            <Line key={p} type="monotone" dataKey={p} name={p} stroke={platformStyle(p).color}
              strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Deeper analytics: market share, productivity leaders, best day, growth trend — all derived from existing data.
const PlatformsInsights = ({ platforms, total, month, from, to }) => {
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    let alive = true;
    api.ridersPlatformsTrend(month, from, to).then((r) => { if (alive) setTrend(r); }).catch(() => {});
    return () => { alive = false; };
  }, [month, from, to]);

  const active = (platforms || []).filter((p) => p.has_data && p.completed > 0);
  if (active.length < 2) return null;

  const share = (p) => (total.completed > 0 ? (p.completed / total.completed) * 100 : 0);
  const perRider = (p) => (p.riders > 0 ? p.completed / p.riders : 0);
  const perHour = (p) => (p.hours > 0 ? p.completed / p.hours : 0);
  const maxBy = (fn) => active.reduce((a, b) => (fn(b) > fn(a) ? b : a), active[0]);
  const leader = maxBy((p) => p.completed);
  const topRider = maxBy(perRider);
  const topHour = maxBy(perHour);

  const stat = {};
  if (trend?.series?.length) {
    for (const p of active) {
      const vals = trend.series.map((row) => ({ date: row.date, v: Number(row[p.platform] || 0) }));
      const nonzero = vals.filter((x) => x.v > 0);
      const best = vals.reduce((a, b) => (b.v > a.v ? b : a), vals[0]);
      const first = nonzero[0]?.v || 0;
      const last = nonzero[nonzero.length - 1]?.v || 0;
      const growth = first > 0 && nonzero.length > 1 ? ((last - first) / first) * 100 : null;
      stat[p.platform] = { best, growth };
    }
  }

  const sorted = [...active].sort((a, b) => b.completed - a.completed);

  const Highlight = ({ icon: Icon, label, p, value, cls }) => {
    const st = platformStyle(p.platform);
    return (
      <div className={`rounded-2xl border p-3 ${cls}`} data-testid={`insight-highlight-${label}`}>
        <div className="flex items-center gap-1.5 text-[11px] font-semibold opacity-80"><Icon className="h-3.5 w-3.5" />{label}</div>
        <div className="mt-1.5 flex items-center gap-2">
          {st.logo ? <img src={st.logo} alt="" className="h-6 w-6 rounded object-contain" /> : <span className="text-lg">{st.emoji}</span>}
          <span className="font-heading font-bold text-sm" style={{ color: st.text }}>{p.platform}</span>
        </div>
        <div className="mt-1 font-mono-nums text-xl font-extrabold" style={{ color: st.color }}>{value}</div>
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-5" data-testid="platforms-insights">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-blue-600" />
        <h4 className="font-heading font-bold text-sm text-slate-700">تحليل ذكي للمقارنة</h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Highlight icon={Trophy} label="الأكثر طلبات" p={leader} value={`${nf(leader.completed)} طلب`} cls="border-amber-200 bg-amber-50 text-amber-700" />
        <Highlight icon={Users} label="الأعلى إنتاجية للمندوب" p={topRider} value={`${nf(Math.round(perRider(topRider)))} طلب/مندوب`} cls="border-blue-200 bg-blue-50 text-blue-700" />
        <Highlight icon={Zap} label="الأعلى كفاءة بالساعة" p={topHour} value={`${perHour(topHour).toFixed(1)} طلب/ساعة`} cls="border-emerald-200 bg-emerald-50 text-emerald-700" />
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-500 mb-2">الحصة السوقية من إجمالي الطلبات</div>
        <div className="space-y-2">
          {sorted.map((p) => {
            const st = platformStyle(p.platform); const s = share(p);
            return (
              <div key={p.platform} className="flex items-center gap-2" data-testid={`insight-share-${p.platform}`}>
                <div className="w-16 sm:w-20 shrink-0 text-xs font-semibold truncate" style={{ color: st.text }}>{p.platform}</div>
                <div className="flex-1 h-5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full flex items-center justify-end pe-2 min-w-[2.5rem]" style={{ width: `${Math.max(s, 8)}%`, background: st.color }}>
                    <span className="text-[10px] font-bold text-white font-mono-nums">{s.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {trend?.series?.length ? (
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-2">أعلى يوم أداءً لكل تطبيق</div>
          <div className="flex flex-wrap gap-2">
            {sorted.map((p) => {
              const b = stat[p.platform]?.best; if (!b || !b.v) return null;
              const st = platformStyle(p.platform);
              return (
                <div key={p.platform} className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs" style={{ borderColor: st.ring }}>
                  {st.logo ? <img src={st.logo} alt="" className="h-4 w-4 rounded object-contain" /> : <span>{st.emoji}</span>}
                  <span className="font-semibold" style={{ color: st.text }}>{p.platform}</span>
                  <span className="text-slate-300">·</span>
                  <span className="font-mono-nums text-slate-500">{String(b.date).slice(5)}</span>
                  <span className="font-mono-nums font-bold" style={{ color: st.color }}>{nf(b.v)}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <div className="min-w-[620px]">
          <div className="grid grid-cols-[1.3fr_0.9fr_0.8fr_1fr_1fr_0.9fr] bg-slate-50 rounded-t-xl text-[11px] font-semibold text-slate-500">
            <div className="px-2 py-2">التطبيق</div>
            <div className="px-2 py-2 text-center">الطلبات</div>
            <div className="px-2 py-2 text-center">الحصة</div>
            <div className="px-2 py-2 text-center">طلب/مندوب</div>
            <div className="px-2 py-2 text-center">طلب/ساعة</div>
            <div className="px-2 py-2 text-center">الاتجاه</div>
          </div>
          {sorted.map((p) => {
            const st = platformStyle(p.platform);
            const g = stat[p.platform]?.growth;
            return (
              <div key={p.platform} data-testid={`insight-row-${p.platform}`}
                className="grid grid-cols-[1.3fr_0.9fr_0.8fr_1fr_1fr_0.9fr] items-center border-b border-slate-100 last:border-0 text-sm">
                <div className="px-2 py-2 flex items-center gap-1.5 min-w-0">
                  {st.logo ? <img src={st.logo} alt="" className="h-5 w-5 rounded object-contain shrink-0" /> : <span>{st.emoji}</span>}
                  <span className="font-semibold truncate" style={{ color: st.text }}>{p.platform}</span>
                </div>
                <div className="px-2 py-2 text-center font-mono-nums font-bold text-slate-800">{nf(p.completed)}</div>
                <div className="px-2 py-2 text-center font-mono-nums text-slate-600">{share(p).toFixed(1)}%</div>
                <div className="px-2 py-2 text-center font-mono-nums text-slate-600">{p.riders > 0 ? nf(Math.round(perRider(p))) : "—"}</div>
                <div className="px-2 py-2 text-center font-mono-nums text-slate-600">{p.hours > 0 ? perHour(p).toFixed(1) : "—"}</div>
                <div className="px-2 py-2 text-center">
                  {g === null || g === undefined ? <span className="text-slate-300">—</span> : (
                    <span className={`inline-flex items-center gap-0.5 font-mono-nums font-bold text-xs ${g >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {g >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(g).toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
